import { useDebouncedValue } from '@mantine/hooks'
import { FunctionComponent, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Button from '~/components/Button'
import { BaseInputElement } from '~/components/Input'
import { PageContent } from '~/components/PageContent'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { ArchivedAdmAdUnitsTable } from '~/modules/adm/inventory/adunits/ArchivedAdmAdUnitsTable'
import { ArchivedPbsAdUnitsTable } from '~/modules/pbs/adunits/ArchivedPbsAdUnitsTable'
import { AdUnitTab } from './adUnitTab'

const SEARCH_DEBOUNCE_TIME = 300

type Props = {
  tab: AdUnitTab
}

/**
 * The archived ad units of one tab, on their own page rather than as a filter on the list.
 *
 * Archiving is meant to make an ad unit go away, so the list a publisher works in every day shows only
 * the live ones and never offers an "Archived" option that could be left on by accident. This page is
 * where the archived ones are, and reaching it is owner-only — enforced by the route's `accessRole` and
 * again by the API, which refuses the `enabled` transition to anyone below owner.
 *
 * Restoring is the owner's too: unarchiving republishes the ad unit into every stack that used to carry
 * it, so it asks for the ad unit's name to be typed back first. Same shape as the archived medias page.
 *
 * One page per tab rather than one page listing all four: the tabs are two different products with two
 * different tables behind them, and an archived row is only ever restored back into the tab it left.
 */
export const ArchivedAdUnitsPage: FunctionComponent<Props> = ({ tab }) => {
  const navigate = useNavigate()
  const breadcrumbs = useBreadcrumbs()
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebouncedValue(search, SEARCH_DEBOUNCE_TIME)

  return (
    <PageContent breadcrumbs={breadcrumbs}>
      <div className={'flex flex-col gap-4'}>
        {/* Back sits where the ad unit list keeps its overflow menu — top right, above the table. The two
            pages are the same list twice, so the control that swaps between them stays in one place. */}
        <div className={'flex flex-wrap items-center gap-4'}>
          <BaseInputElement type={'text'} iconLeft={'search'} labelIsPlaceholder label={'Search an ad unit'} value={search} onChange={(event) => setSearch(event.target.value)} />
          <div className={'flex-1'} />
          <Button variant={'tertiary'} iconName={'chevron_left'} onClick={() => navigate(`/monetization/inventory/adunits/${tab.path}`)} trackingLabel={'common-archived-ad-units-back-navigate'}>
            Back to ad units
          </Button>
        </div>

        {/* The same split `AdUnitsTab` makes: Web is One's own inventory and has no `env` at all. */}
        {tab.env ? <ArchivedPbsAdUnitsTable env={tab.env} search={debouncedSearch} /> : <ArchivedAdmAdUnitsTable search={debouncedSearch} />}
      </div>
    </PageContent>
  )
}
